/**
 * Canvas Layout Transform Utilities
 *
 * Shared transform functions used by both useCanvasLayoutQuery and
 * useCanvasLayoutMutations. Extracted to avoid a circular dependency
 * between those two modules.
 */

import {
  type CanvasDefinitionInput,
  type WidgetInput,
  CanvasLayoutType,
  WidgetType,
} from "~/graphql/generated/graphql";
import type { CanvasConfig } from "~/helpers/canvas/canvas-types";

/**
 * Known top-level widget fields that map to explicit WidgetInput fields.
 * Derived from the generated GraphQL type so it stays in sync with the schema.
 * Everything else gets collected into the `props` JSON bag.
 */
type KnownWidgetField = Exclude<keyof WidgetInput, "props">;
const KNOWN_WIDGET_FIELDS = new Set<string>(
  Object.keys({
    name: true,
    label: true,
    dataSource: true,
    type: true,
    position: true,
    filterBindings: true,
    colorScheme: true,
    hideOnBreakpoints: true,
  } satisfies Record<KnownWidgetField, true>),
);

const BREAKPOINTS = ["xs", "sm", "md", "lg", "xl"] as const;

/**
 * Transforms frontend responsive position overrides into the backend input shape.
 */
function transformResponsivePositionToInput(responsive: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const bp of BREAKPOINTS) {
    const override = responsive[bp.toUpperCase()];
    if (override) {
      result[bp] = {
        column: override.column ?? null,
        row: override.row ?? null,
        columnSpan: override.columnSpan ?? null,
        rowSpan: override.rowSpan ?? null,
      };
    }
  }
  return result;
}

/**
 * Transforms a frontend CanvasConfig into the backend mutation input shape.
 * Since the frontend now uses backend enum values directly, no type mapping is needed.
 * Widget-specific properties (format, icon, chartType, etc.) are collected into `props`.
 */
export function transformConfigToInput(config: CanvasConfig): { layoutType: CanvasLayoutType; layoutProps?: any; variables?: any; canvases: CanvasDefinitionInput[] } {
  const canvases: CanvasDefinitionInput[] = (config.canvases ?? []).map((canvas) => ({
    name: canvas.name,
    layout: canvas.layout ?? null,
    widgets: (canvas.widgets ?? []).map((widget: any): WidgetInput => {
      // Collect unknown fields into props
      const props: Record<string, any> = {};
      for (const [key, value] of Object.entries(widget)) {
        if (!KNOWN_WIDGET_FIELDS.has(key)) {
          props[key] = value;
        }
      }

      return {
        name: widget.name,
        label: widget.label ?? null,
        dataSource: widget.dataSource ?? null,
        type: widget.type as WidgetType,
        position: widget.position
          ? {
              column: widget.position.column,
              row: widget.position.row,
              columnSpan: widget.position.columnSpan ?? null,
              rowSpan: widget.position.rowSpan ?? null,
              responsive: widget.position.responsive
                ? transformResponsivePositionToInput(widget.position.responsive)
                : null,
            }
          : null,
        filterBindings: widget.filterBindings ?? null,
        colorScheme: widget.colorScheme ?? null,
        hideOnBreakpoints: widget.hideOnBreakpoints ?? null,
        props: Object.keys(props).length > 0 ? props : null,
      };
    }),
  }));

  return {
    layoutType: config.layoutType as CanvasLayoutType,
    layoutProps: config.layoutProps ?? null,
    variables: config.variables ?? null,
    canvases,
  };
}
